import {
  WARRANTY_CLASSIFICATION_ID,
  TIME_WARRANTY_TYPE,
} from "../component/app/admin/dealership/tabs/warrantyConstants";

export const DEFAULT_ANNUAL_MILES = 15000;
export const DEFAULT_YEARS_OWNED = 7;
export const MILES_STEP = 1000;

/** Intro timing (ms) — each property reveals 300ms after the previous. */
export const INTRO_MINOR_DELAY = 180;
export const INTRO_BAR_STAGGER = 300;
export const INTRO_BAR_DURATION = 700;

export const isTimeCoverage = (coverage) =>
  (coverage?.warranty_type_en || "").toUpperCase() === TIME_WARRANTY_TYPE.en;

/**
 * PRD calc for major coverages.
 * Minor coverages use dealer months directly (no mileage formula).
 */
export const computeBar = (coverage, annualMiles, yearsOwned) => {
  const ownershipMonths = yearsOwned * 12;
  const coverageMonths = Number(coverage?.coverage_months) || 0;
  const coverageMiles = Number(coverage?.coverage_miles) || 0;
  const isMinor =
    coverage?.warranty_classification_id === WARRANTY_CLASSIFICATION_ID.MINOR;

  let coveredMonths;

  if (isMinor) {
    coveredMonths = Math.min(coverageMonths, ownershipMonths);
  } else {
    const mileageBasedMonths =
      annualMiles > 0
        ? Math.round((coverageMiles / annualMiles) * 12)
        : coverageMonths;
    coveredMonths = Math.min(coverageMonths, mileageBasedMonths);
  }

  coveredMonths = Math.max(0, Math.min(coveredMonths, ownershipMonths));
  const uncoveredMonths = Math.max(0, ownershipMonths - coveredMonths);

  return {
    coverageId: coverage.id,
    name: coverage.warranty_type_en || "Coverage",
    nameEs: coverage.warranty_type_es || coverage.warranty_type_en,
    infoEn: coverage.info_description_en || "",
    infoEs: coverage.info_description_es || "",
    infoTitleEn: coverage.info_title_en || "",
    infoTitleEs: coverage.info_title_es || "",
    infoSubtitleEn: coverage.info_subtitle_en || "",
    infoSubtitleEs: coverage.info_subtitle_es || "",
    infoFooterEn: coverage.info_footer_en || "",
    infoFooterEs: coverage.info_footer_es || "",
    infoItems: coverage.information_items || [],
    covered: coveredMonths,
    uncovered: uncoveredMonths,
    total: ownershipMonths,
  };
};

export const buildMinorItems = (coverages, annualMiles, yearsOwned, isSpanish) =>
  (coverages || [])
    .filter(
      (c) =>
        c.warranty_classification_id === WARRANTY_CLASSIFICATION_ID.MINOR &&
        !isTimeCoverage(c),
    )
    .map((c) => {
      const bar = computeBar(c, annualMiles, yearsOwned);
      return {
        id: c.id,
        name: isSpanish ? bar.nameEs || bar.name : bar.name,
        months: bar.covered,
      };
    });

/**
 * Build major chart rows. Optional savedItems override covered/uncovered.
 */
export const buildChartData = (
  coverages,
  annualMiles,
  yearsOwned,
  isSpanish,
  savedItems = null,
) => {
  const major = (coverages || []).filter(
    (c) =>
      c.warranty_classification_id === WARRANTY_CLASSIFICATION_ID.MAJOR &&
     !isTimeCoverage(c) &&
      c.is_enabled !== false,
  );

  const savedByCoverageId = {};
  (savedItems || []).forEach((item) => {
    savedByCoverageId[item.warranty_coverage_id] = item;
  });

  return major.map((coverage) => {
    const bar = computeBar(coverage, annualMiles, yearsOwned);
    const saved = savedByCoverageId[coverage.id];
    const covered =
      saved?.covered_months != null ? Number(saved.covered_months) : bar.covered;
    const uncovered =
      saved?.uncovered_months != null
        ? Number(saved.uncovered_months)
        : bar.uncovered;

    return {
      ...bar,
      covered,
      uncovered,
      total: yearsOwned * 12,
      name: isSpanish ? bar.nameEs || bar.name : bar.name,
      info: isSpanish ? bar.infoEs || bar.infoEn : bar.infoEn,
      infoTitle: isSpanish
        ? bar.infoTitleEs || bar.infoTitleEn
        : bar.infoTitleEn,
      infoSubtitle: isSpanish
        ? bar.infoSubtitleEs || bar.infoSubtitleEn
        : bar.infoSubtitleEn,
      infoFooter: isSpanish
        ? bar.infoFooterEs || bar.infoFooterEn
        : bar.infoFooterEn,
      infoItems: bar.infoItems,
    };
  });
};

export const mapExclusionsByLetter = (exclusions = []) => {
  const map = {};
  (exclusions || []).forEach((ex) => {
    const letter = (ex.uncoverage_type || "").toUpperCase();
    if (letter) map[letter] = ex;
  });
  return map;
};
